import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { ChevronDown, ChevronUp, Download, Paperclip } from 'lucide-react';
import type {
  OpsTicketAttachment,
  OpsTicketReply,
  OpsTicketSortKey,
  OpsTicketSummary,
  TicketStatus,
  TicketStatusFilter,
  TicketType,
} from '@nadoutong/contracts';
import {
  OPS_TICKET_SORT_DEFAULT_DIR,
  OPS_TICKET_SORT_DEFAULT_KEY,
  TICKET_STATUSES,
  isTicketStatus,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { downloadTicketAttachment } from '../api/supportTickets.ts';
import {
  useCreateVendorReply,
  useSupportTicket,
  useSupportTickets,
  useTicketAttachment,
  useUpdateTicketStatus,
} from '../api/useSupportTickets.ts';
import { CustomerCell } from '../components/identity/CustomerCell.tsx';
import { CustomerRef } from '../components/identity/CustomerRef.tsx';
import { OpsUserRef } from '../components/identity/OpsUserRef.tsx';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { base64ToBytes } from '../lib/receipts.ts';
import {
  Button,
  Chip,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  Skeleton,
  SortableTh,
  TBody,
  THead,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
  cx,
} from '../ui/index.ts';
import type { ChipTone, SelectOption, TableSort } from '../ui/index.ts';

/**
 * `/support/tickets` — the operator half of the support-ticket system
 * (#1394). The queue lists every tenant ticket; expanding a row loads
 * the detail (description + conversation + attachments) and offers the
 * two mutations: a status change and an UNBOUNDED vendor reply
 * (operator answers carry error output and step-by-step instructions,
 * deliberately not subject to the tenant-side 500-char cap).
 *
 * This is the ticket system, not the request-log investigation tool —
 * `/support/requests` deliberately shares only the role gate.
 *
 * Tooling conventions (matches SupportRequests):
 *   - Draft vs. applied state: clicking a status pill or typing doesn't
 *     issue a request; the operator must click Apply.
 *   - Pagination: offset-based (Previous/Next). Filter changes reset
 *     offset to 0.
 *   - Ticket statuses render as Dispatch Deck chips (open warn /
 *     processing signal / resolved ok / closed neutral).
 *   - Sorting is SERVER-SIDE and travels in the query (#1946). The list is
 *     paginated, so reordering the page in the browser would sort fifty rows
 *     and present it as a sorted queue. Changing the sort resets the offset
 *     for the same reason a filter change does: page 3 of one ordering has
 *     nothing to do with page 3 of another.
 */

const PAGE_LIMIT = 50;

interface FilterState {
  status: TicketStatusFilter;
  orgId: string;
  q: string;
}

const EMPTY_FILTERS: FilterState = {
  status: 'all',
  orgId: '',
  q: '',
};

const STATUS_FILTERS: readonly TicketStatusFilter[] = ['all', ...TICKET_STATUSES];

const STATUS_LABEL_KEYS: Record<TicketStatusFilter, I18nKey> = {
  all: 'support.tickets.status.all',
  open: 'support.tickets.status.open',
  processing: 'support.tickets.status.processing',
  resolved: 'support.tickets.status.resolved',
  closed: 'support.tickets.status.closed',
};

const TYPE_LABEL_KEYS: Record<TicketType, I18nKey> = {
  billing: 'support.tickets.type.billing',
  usage: 'support.tickets.type.usage',
  api: 'support.tickets.type.api',
  model: 'support.tickets.type.model',
  other: 'support.tickets.type.other',
};

// Same pill chrome as the PaymentsOrders status filter.
const FILTER_PILL_BASE =
  'inline-flex select-none items-center whitespace-nowrap rounded-full border px-3 py-1 text-micro font-medium transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 max-md:min-h-9 max-md:px-3';
const FILTER_PILL_ACTIVE = 'border-signal-deep/50 bg-signal-dim text-signal';
const FILTER_PILL_IDLE =
  'border-line bg-transparent text-paper-mid hover:border-line-strong hover:text-paper';

function statusChipTone(status: TicketStatus): ChipTone {
  if (status === 'open') return 'warn';
  if (status === 'processing') return 'signal';
  if (status === 'resolved') return 'ok';
  return 'neutral';
}

function truncateId(value: string, head = 10, tail = 4): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

function formatSize(sizeBytes: number): string {
  if (sizeBytes >= 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (sizeBytes >= 1024) {
    return `${(sizeBytes / 1024).toFixed(0)} KB`;
  }
  return `${sizeBytes} B`;
}

/**
 * One conversation entry. Vendor replies are visually distinct (signal
 * accent) and carry the in-row operator attribution (#1397 delta);
 * user replies stay on the plain recessed surface.
 */
function ReplyItem({ reply }: { readonly reply: OpsTicketReply }): JSX.Element {
  const t = useT();
  const isVendor = reply.author === 'vendor';
  return (
    <li
      data-testid={`support-ticket-reply-${reply.id}`}
      className={cx(
        'rounded-lg border p-3',
        isVendor ? 'border-signal-deep/50 bg-signal-dim' : 'border-line-soft bg-well',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cx(
            'text-label font-semibold',
            isVendor ? 'text-signal' : 'text-paper-mid',
          )}
          data-testid={`support-ticket-reply-${reply.id}-author`}
        >
          {isVendor
            ? t('support.tickets.reply.author.vendor')
            : t('support.tickets.reply.author.user')}
        </span>
        {isVendor && reply.ops_user_id !== null ? (
          <span
            className="font-mono text-micro text-paper-low"
            data-testid={`support-ticket-reply-${reply.id}-ops-user`}
          >
            <OpsUserRef id={reply.ops_user_id} />
          </span>
        ) : null}
        <span className="ml-auto font-mono text-micro text-paper-low">
          {formatLocalTimestamp(reply.created_at, { seconds: true })}
        </span>
      </div>
      <p className="m-0 mt-1.5 whitespace-pre-wrap text-dense text-paper">{reply.content}</p>
    </li>
  );
}

/**
 * One attachment. Tenant screenshots ARE the evidence an operator is
 * triaging, so an image renders inline instead of hiding behind a
 * download click — the queue is read far more often than an attachment
 * is saved to disk.
 *
 * The bucket is private by contract (#1394), so there is no public
 * object URL to point an `<img>` at: the bytes come back base64 from
 * `/v1/ops/tickets/attachments/:id` and become a data URL. That is also
 * why the fetch is a React Query entry rather than an on-click call —
 * see `useTicketAttachment` for why it sits outside the ticket root key.
 *
 * A non-image content type keeps the download button. The create
 * contract admits only image mimes today, so that arm is unreachable
 * from the product; it exists so a later allowlist widening (a PDF, a
 * log file) degrades to a working row instead of a broken `<img>`.
 */
function AttachmentItem({
  attachment,
}: {
  readonly attachment: OpsTicketAttachment;
}): JSX.Element {
  return attachment.content_type.startsWith('image/') ? (
    <AttachmentImage attachment={attachment} />
  ) : (
    <AttachmentDownloadRow attachment={attachment} />
  );
}

/** Inline image preview; clicking toggles between the boxed and full size. */
function AttachmentImage({
  attachment,
}: {
  readonly attachment: OpsTicketAttachment;
}): JSX.Element {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const payload = useTicketAttachment(attachment.id, true);
  const toggleLabel = t(
    expanded
      ? 'support.tickets.detail.attachment_collapse'
      : 'support.tickets.detail.attachment_expand',
    { name: attachment.file_name },
  );

  return (
    <li
      data-testid={`support-ticket-attachment-${attachment.id}`}
      className="flex flex-col gap-1.5 rounded-lg border border-line-soft bg-well p-2"
    >
      {payload.isPending ? (
        <>
          <Skeleton
            className="h-40 w-full"
            testId={`support-ticket-attachment-${attachment.id}-loading`}
          />
          <span className="sr-only">{t('support.tickets.detail.attachment_loading_aria')}</span>
        </>
      ) : payload.isError ? (
        <p
          className="m-0 rounded-lg border border-warn-line bg-warn-dim px-3 py-2 text-dense text-warn"
          data-testid={`support-ticket-attachment-${attachment.id}-error`}
        >
          {coerceErrorMessage(payload.error)}
        </p>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          // The button's only child is the <img>, whose `alt` would
          // otherwise become the accessible name — a screen reader would
          // announce the file name and never the action. An explicit
          // label plus `aria-expanded` carries both (DESIGN.md §9).
          aria-label={toggleLabel}
          aria-expanded={expanded}
          title={toggleLabel}
          className="cursor-zoom-in rounded-lg border-none bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
          data-testid={`support-ticket-attachment-${attachment.id}-toggle`}
        >
          <img
            src={`data:${payload.data.attachment.content_type};base64,${payload.data.data_base64}`}
            alt={attachment.file_name}
            className={cx(
              'block w-auto max-w-full rounded-lg object-contain',
              expanded ? 'max-h-none' : 'max-h-64',
            )}
            data-testid={`support-ticket-attachment-${attachment.id}-image`}
          />
        </button>
      )}
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-dense text-paper">{attachment.file_name}</span>
        <span className="font-mono text-micro text-paper-low">
          {attachment.content_type} · {formatSize(attachment.size_bytes)}
        </span>
      </div>
    </li>
  );
}

/**
 * Download fallback for a non-image attachment: fetch the base64
 * payload, decode to a Blob, and trigger a browser download via an
 * object URL (same flow as ReceiptDetailDrawer).
 */
function AttachmentDownloadRow({
  attachment,
}: {
  readonly attachment: OpsTicketAttachment;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { fetchImpl } = useSession();
  const [downloading, setDownloading] = useState(false);

  async function handleDownload(): Promise<void> {
    setDownloading(true);
    try {
      const payload = await downloadTicketAttachment(attachment.id, fetchImpl);
      const bytes = base64ToBytes(payload.data_base64);
      const blob = new Blob([bytes], { type: payload.attachment.content_type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = payload.attachment.file_name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(coerceErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <li
      data-testid={`support-ticket-attachment-${attachment.id}`}
      className="flex items-center justify-between gap-3 rounded-lg border border-line-soft bg-well px-3 py-2"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-dense text-paper">{attachment.file_name}</span>
        <span className="font-mono text-micro text-paper-low">
          {attachment.content_type} · {formatSize(attachment.size_bytes)}
        </span>
      </div>
      <Button
        type="button"
        size="sm"
        loading={downloading}
        onClick={() => void handleDownload()}
        data-testid={`support-ticket-attachment-${attachment.id}-download`}
      >
        <Download aria-hidden="true" className="h-3.5 w-3.5" />
        {t('support.tickets.detail.download')}
      </Button>
    </li>
  );
}

/** Status control: the four ticket statuses, mutation fires on pick. */
function StatusControl({ ticket }: { readonly ticket: OpsTicketSummary }): JSX.Element {
  const t = useT();
  const toast = useToast();
  const updateStatus = useUpdateTicketStatus();
  const options: readonly SelectOption[] = TICKET_STATUSES.map((status) => ({
    value: status,
    label: t(STATUS_LABEL_KEYS[status]),
  }));

  return (
    <FormField
      label={t('support.tickets.detail.status_label')}
      fieldId={`support-ticket-status-${ticket.id}`}
    >
      <Select
        id={`support-ticket-status-${ticket.id}`}
        value={ticket.status}
        onValueChange={(value) => {
          if (!isTicketStatus(value) || value === ticket.status) return;
          updateStatus.mutate(
            { ticketId: ticket.id, status: value },
            {
              onSuccess: () => {
                toast.success(t('support.tickets.toast.status_updated'));
              },
              onError: (err) => {
                toast.error(coerceErrorMessage(err));
              },
            },
          );
        }}
        options={options}
        disabled={updateStatus.isPending}
        testId="support-ticket-status-select"
        className="w-44"
      />
    </FormField>
  );
}

/** Vendor reply composer. Content is required non-empty but UNBOUNDED. */
function ReplyComposer({ ticketId }: { readonly ticketId: string }): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [content, setContent] = useState('');
  const createReply = useCreateVendorReply();

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (content.trim().length === 0) return;
    createReply.mutate(
      { ticketId, content },
      {
        onSuccess: () => {
          setContent('');
          toast.success(t('support.tickets.toast.reply_sent'));
        },
        onError: (err) => {
          toast.error(coerceErrorMessage(err));
        },
      },
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-2"
      data-testid="support-ticket-reply-form"
    >
      <FormField
        label={t('support.tickets.reply.label')}
        fieldId={`support-ticket-reply-${ticketId}`}
        required
      >
        <Textarea
          id={`support-ticket-reply-${ticketId}`}
          value={content}
          rows={3}
          onChange={(e) => setContent(e.currentTarget.value)}
          placeholder={t('support.tickets.reply.placeholder')}
          data-testid="support-ticket-reply-input"
        />
      </FormField>
      <div>
        <Button
          type="submit"
          variant="primary"
          disabled={content.trim().length === 0 || createReply.isPending}
          data-testid="support-ticket-reply-submit"
        >
          {createReply.isPending
            ? t('support.tickets.reply.submitting')
            : t('support.tickets.reply.submit')}
        </Button>
      </div>
    </form>
  );
}

/**
 * Expanded-row body: fetches the detail on expansion (a collapsed page
 * never issues a detail request) and renders description, conversation
 * timeline, attachments, status control, and the reply composer.
 */
function TicketDetails({ ticketId }: { readonly ticketId: string }): JSX.Element {
  const t = useT();
  const detail = useSupportTicket(ticketId);

  if (detail.isPending) {
    return (
      <div className="flex flex-col gap-2" data-testid={`support-ticket-${ticketId}-loading`}>
        {[0, 1].map((i) => (
          <Skeleton key={i} className="h-10" />
        ))}
        <span className="sr-only">{t('support.tickets.detail.loading_aria')}</span>
      </div>
    );
  }

  if (detail.isError) {
    return (
      <p
        role="alert"
        className="m-0 text-dense text-bad"
        data-testid={`support-ticket-${ticketId}-error`}
      >
        {detail.error instanceof OpsApiError
          ? `${detail.error.code}: ${detail.error.message}`
          : t('common.network_error')}
      </p>
    );
  }

  const { ticket, replies, attachments } = detail.data;

  return (
    <div className="flex flex-col gap-4 rounded-lg bg-well p-3">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-label text-paper-low">
            {t('support.tickets.detail.description')}
          </span>
          <p
            className="m-0 whitespace-pre-wrap text-dense text-paper"
            data-testid={`support-ticket-${ticketId}-description`}
          >
            {ticket.description}
          </p>
        </div>
        <StatusControl ticket={ticket} />
      </div>

      <section className="flex flex-col gap-2">
        <h3 className="m-0 text-section text-paper">
          {t('support.tickets.detail.conversation')}
        </h3>
        {replies.length === 0 ? (
          <p
            className="m-0 text-dense text-paper-low"
            data-testid={`support-ticket-${ticketId}-conversation-empty`}
          >
            {t('support.tickets.detail.conversation.empty')}
          </p>
        ) : (
          <ol
            className="m-0 flex list-none flex-col gap-2 p-0"
            data-testid={`support-ticket-${ticketId}-conversation`}
          >
            {replies.map((reply) => (
              <ReplyItem key={reply.id} reply={reply} />
            ))}
          </ol>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="m-0 inline-flex items-center gap-1.5 text-section text-paper">
          <Paperclip aria-hidden="true" className="h-3.5 w-3.5" />
          {t('support.tickets.detail.attachments')}
        </h3>
        {attachments.length === 0 ? (
          <p
            className="m-0 text-dense text-paper-low"
            data-testid={`support-ticket-${ticketId}-attachments-empty`}
          >
            {t('support.tickets.detail.attachments.none')}
          </p>
        ) : (
          <ul
            className="m-0 flex list-none flex-col gap-1.5 p-0"
            data-testid={`support-ticket-${ticketId}-attachments`}
          >
            {attachments.map((attachment) => (
              <AttachmentItem key={attachment.id} attachment={attachment} />
            ))}
          </ul>
        )}
      </section>

      <ReplyComposer ticketId={ticketId} />
    </div>
  );
}

interface RowProps {
  readonly ticket: OpsTicketSummary;
  readonly expanded: boolean;
  readonly onToggle: (id: string) => void;
}

function TicketRow({ ticket, expanded, onToggle }: RowProps): JSX.Element {
  const t = useT();
  return (
    <>
      <Tr
        data-testid={`support-ticket-row-${ticket.id}`}
        className="cursor-pointer"
        onClick={() => onToggle(ticket.id)}
      >
        <Td mono emphasis title={ticket.id} data-testid={`support-ticket-row-${ticket.id}-id`}>
          {truncateId(ticket.id)}
        </Td>
        <Td title={ticket.org_id} data-testid={`support-ticket-row-${ticket.id}-org`}>
          <CustomerCell
            orgId={ticket.org_id}
            testId={`support-ticket-customer-${ticket.id}`}
            secondary={(
              <span className="self-start font-mono">
                <CustomerRef orgId={ticket.org_id} withCompany={false} />
              </span>
            )}
          />
        </Td>
        <Td data-testid={`support-ticket-row-${ticket.id}-type`}>
          {t(TYPE_LABEL_KEYS[ticket.type])}
        </Td>
        <Td data-testid={`support-ticket-row-${ticket.id}-status`}>
          <Chip tone={statusChipTone(ticket.status)}>{t(STATUS_LABEL_KEYS[ticket.status])}</Chip>
        </Td>
        <Td data-testid={`support-ticket-row-${ticket.id}-email`}>{ticket.email}</Td>
        <Td num data-testid={`support-ticket-row-${ticket.id}-attachments`}>
          {ticket.attachment_count}
        </Td>
        <Td
          mono
          title={formatLocalTimestamp(ticket.created_at, { seconds: true })}
          data-testid={`support-ticket-row-${ticket.id}-created`}
        >
          {formatLocalTimestamp(ticket.created_at, { seconds: true })}
        </Td>
        <Td
          mono
          title={formatLocalTimestamp(ticket.updated_at, { seconds: true })}
          data-testid={`support-ticket-row-${ticket.id}-updated`}
        >
          {formatLocalTimestamp(ticket.updated_at, { seconds: true })}
        </Td>
        <Td className="text-right">
          {expanded ? (
            <ChevronUp aria-hidden="true" className="inline h-4 w-4 text-paper-low" />
          ) : (
            <ChevronDown aria-hidden="true" className="inline h-4 w-4 text-paper-low" />
          )}
        </Td>
      </Tr>
      {expanded ? (
        <Tr interactive={false} data-testid={`support-ticket-${ticket.id}-details`}>
          <Td colSpan={9}>
            <TicketDetails ticketId={ticket.id} />
          </Td>
        </Tr>
      ) : null}
    </>
  );
}

export function SupportTickets(): JSX.Element {
  const t = useT();
  const [draft, setDraft] = useState<FilterState>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<FilterState>(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sort, setSort] = useState<TableSort<OpsTicketSortKey>>({
    key: OPS_TICKET_SORT_DEFAULT_KEY,
    dir: OPS_TICKET_SORT_DEFAULT_DIR,
  });

  const query = useSupportTickets({
    status: applied.status === 'all' ? undefined : applied.status,
    org_id: applied.orgId.length > 0 ? applied.orgId : undefined,
    q: applied.q.length > 0 ? applied.q : undefined,
    limit: PAGE_LIMIT,
    offset,
    sort: sort.key,
    dir: sort.dir,
  });

  // The header row describes THE ROWS ON SCREEN, not the request in flight.
  //
  // `keepPreviousData` deliberately keeps the previous ordering's rows mounted
  // while the new one is fetched, so driving `aria-sort` and the arrow off
  // `sort` would announce a column the visible rows are not ordered by for
  // the length of the round trip. `aria-sort` is a factual claim to assistive
  // tech about the data underneath it, not a highlight on the control that
  // was clicked — a screen-reader user who takes it at face value reads the
  // top row as the newest ticket when it is still the oldest.
  //
  // `isPlaceholderData` is exactly the "these rows belong to the previous
  // query key" signal, so the rendered sort catches up when the data does.
  // Adjusting state during render (rather than in an effect) means the header
  // and the rows update in the same commit; a flash of disagreement is the
  // thing being fixed.
  const [renderedSort, setRenderedSort] = useState(sort);
  if (
    !query.isPlaceholderData &&
    (renderedSort.key !== sort.key || renderedSort.dir !== sort.dir)
  ) {
    setRenderedSort(sort);
  }

  const tickets = useMemo(() => query.data?.tickets ?? [], [query.data]);
  const total = query.data?.total ?? 0;
  const currentOffset = query.data?.offset ?? offset;
  const currentLimit = query.data?.limit ?? PAGE_LIMIT;
  const hasPrev = currentOffset > 0;
  const hasNext = currentOffset + currentLimit < total;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setApplied({
      status: draft.status,
      orgId: draft.orgId.trim(),
      q: draft.q.trim(),
    });
    setOffset(0);
    setExpandedId(null);
  }

  function handleReset(): void {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setOffset(0);
    setExpandedId(null);
  }

  function handleToggle(id: string): void {
    setExpandedId((current) => (current === id ? null : id));
  }

  function handleSort(key: OpsTicketSortKey): void {
    // First click on a new column opens it descending — for the timestamps
    // and the attachment count that is the interesting end, and holding one
    // rule keeps the headers predictable.
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' },
    );
    setOffset(0);
    setExpandedId(null);
  }

  return (
    <section data-testid="support-tickets-page" className="flex flex-col gap-6">
      <PageHeader title={t('support.tickets.title')} sub={t('support.tickets.subtitle')} />

      <form
        onSubmit={handleSubmit}
        data-testid="support-tickets-filters"
        className="flex flex-col gap-4 rounded-lg border border-line-soft bg-card p-4"
      >
        <div
          className="flex flex-wrap items-center gap-2"
          role="group"
          aria-label={t('support.tickets.filter.status')}
        >
          <span className="text-label text-paper-mid">{t('support.tickets.filter.status')}</span>
          {STATUS_FILTERS.map((status) => {
            const active = draft.status === status;
            return (
              <button
                key={status}
                type="button"
                onClick={() => setDraft({ ...draft, status })}
                data-testid={`support-tickets-status-${status}`}
                aria-pressed={active}
                className={cx(FILTER_PILL_BASE, active ? FILTER_PILL_ACTIVE : FILTER_PILL_IDLE)}
              >
                {t(STATUS_LABEL_KEYS[status])}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <FormField
            label={t('support.tickets.filter.org_id')}
            fieldId="support-tickets-filter-org-id"
          >
            <Input
              id="support-tickets-filter-org-id"
              type="text"
              mono
              value={draft.orgId}
              onChange={(e) => setDraft({ ...draft, orgId: e.currentTarget.value })}
              placeholder={t('support.tickets.filter.org_id.placeholder')}
              data-testid="support-tickets-filter-org-id"
              className="w-64"
            />
          </FormField>
          <FormField label={t('support.tickets.filter.q')} fieldId="support-tickets-filter-q">
            <Input
              id="support-tickets-filter-q"
              type="text"
              value={draft.q}
              onChange={(e) => setDraft({ ...draft, q: e.currentTarget.value })}
              placeholder={t('support.tickets.filter.q.placeholder')}
              data-testid="support-tickets-filter-q"
              className="w-64"
            />
          </FormField>
          <div className="flex h-9 items-center gap-2">
            <Button type="submit" variant="primary" data-testid="support-tickets-filter-apply">
              {t('support.tickets.filter.apply')}
            </Button>
            <Button type="button" onClick={handleReset} data-testid="support-tickets-filter-reset">
              {t('support.tickets.filter.reset')}
            </Button>
          </div>
        </div>
      </form>

      {query.isError ? (
        <div
          role="alert"
          data-testid="support-tickets-error"
          className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3"
        >
          <div className="flex flex-col gap-1">
            <span className="text-body font-semibold text-bad">
              {t('support.tickets.error.title')}
            </span>
            <span className="text-dense text-bad-bright">
              {query.error instanceof OpsApiError
                ? `${query.error.code}: ${query.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => void query.refetch()}
            data-testid="support-tickets-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {query.isPending ? (
        <div
          data-testid="support-tickets-loading"
          className="flex flex-col gap-2 rounded-lg border border-line-soft bg-card p-4"
        >
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12" />
          ))}
          <span className="sr-only">{t('support.tickets.loading_aria')}</span>
        </div>
      ) : null}

      {query.isSuccess && tickets.length === 0 ? (
        <EmptyState
          testId="support-tickets-empty"
          className="rounded-lg border border-line-soft bg-card"
          title={t('support.tickets.empty.title')}
          hint={t('support.tickets.empty.body')}
        />
      ) : null}

      {query.isSuccess && tickets.length > 0 ? (
        <>
          <Table testId="support-tickets-table">
            <THead>
              <Tr interactive={false}>
                <SortableTh
                  label={t('support.tickets.column.id')}
                  sortKey="id"
                  sort={renderedSort}
                  onSort={handleSort}
                  disabled={query.isFetching}
                  testId="support-tickets-sort-id"
                />
                <SortableTh
                  label={t('support.tickets.column.org')}
                  sortKey="org_id"
                  sort={renderedSort}
                  onSort={handleSort}
                  disabled={query.isFetching}
                  testId="support-tickets-sort-org"
                />
                <SortableTh
                  label={t('support.tickets.column.type')}
                  sortKey="type"
                  sort={renderedSort}
                  onSort={handleSort}
                  disabled={query.isFetching}
                  testId="support-tickets-sort-type"
                />
                <SortableTh
                  label={t('support.tickets.column.status')}
                  sortKey="status"
                  sort={renderedSort}
                  onSort={handleSort}
                  disabled={query.isFetching}
                  testId="support-tickets-sort-status"
                />
                <SortableTh
                  label={t('support.tickets.column.email')}
                  sortKey="email"
                  sort={renderedSort}
                  onSort={handleSort}
                  disabled={query.isFetching}
                  testId="support-tickets-sort-email"
                />
                <SortableTh
                  num
                  label={t('support.tickets.column.attachments')}
                  sortKey="attachment_count"
                  sort={renderedSort}
                  onSort={handleSort}
                  disabled={query.isFetching}
                  testId="support-tickets-sort-attachments"
                />
                <SortableTh
                  label={t('support.tickets.column.created_at')}
                  sortKey="created_at"
                  sort={renderedSort}
                  onSort={handleSort}
                  disabled={query.isFetching}
                  testId="support-tickets-sort-created"
                />
                <SortableTh
                  label={t('support.tickets.column.updated_at')}
                  sortKey="updated_at"
                  sort={renderedSort}
                  onSort={handleSort}
                  disabled={query.isFetching}
                  testId="support-tickets-sort-updated"
                />
                <Th className="text-right" />
              </Tr>
            </THead>
            <TBody>
              {tickets.map((ticket) => (
                <TicketRow
                  key={ticket.id}
                  ticket={ticket}
                  expanded={expandedId === ticket.id}
                  onToggle={handleToggle}
                />
              ))}
            </TBody>
          </Table>
          <nav
            aria-label={t('support.tickets.pagination.aria_label')}
            data-testid="support-tickets-pagination"
            className="flex items-center justify-between gap-4 rounded-lg border border-line-soft bg-card px-4 py-3"
          >
            <span className="text-dense text-paper-low">
              {t('support.tickets.pagination.range', {
                start: currentOffset + 1,
                end: Math.min(currentOffset + tickets.length, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!hasPrev || query.isFetching}
                onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
                data-testid="support-tickets-pagination-prev"
              >
                {t('common.previous')}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!hasNext || query.isFetching}
                onClick={() => setOffset(offset + PAGE_LIMIT)}
                data-testid="support-tickets-pagination-next"
              >
                {t('common.next')}
              </Button>
            </div>
          </nav>
        </>
      ) : null}
    </section>
  );
}
